import React, { createContext, useContext, useEffect, useRef, useState } from 'react'
import ReactDom from 'react-dom'
import PropTypes from 'prop-types'
import clsx from 'clsx'
import Header from './ModalHeader'
import Body from './ModalBody'
import Footer from './ModalFooter'

const ModalContext = createContext(null)
export const useModal = () => useContext(ModalContext)

export default function Modal({ children, open, size, title, content, iframeSrc, hasCloseButton, onOpen, onClose }) {
  const [isOpen, setIsOpen] = useState(open)
  const ref = useRef(null)
  const showExtraHeader = !React.Children.toArray(children).find(c => c.type === Header) && (title || hasCloseButton)
  const showExtraBody = !React.Children.toArray(children).find(c => c.type === Body) && (content || iframeSrc)

  const close = () => {
    setIsOpen(false)
  }

  const onClickHandler = e => {
    if (e.target === ref.current) close()
  }

  useEffect(() => {
    if (open !== isOpen) setIsOpen(open)
  }, [open])

  useEffect(() => {
    if (isOpen) {
      onOpen && onOpen()
    } else {
      onClose && onClose()
    }
  }, [isOpen])

  useEffect(() => {
    if (!ref || !ref.current) return
    Array(...ref.current.querySelectorAll('[data-modal-close]')).forEach(closer => {
      closer.addEventListener('click', close)
    })
  }, [ref, ref.current])

  return (
    <>
      {ReactDom.createPortal(
        <ModalContext.Provider value={{ title, content, iframeSrc, hasCloseButton }}>
          <div
            ref={ref}
            className={clsx('modal-lite', !iframeSrc && 'modal-lite-iframe-none', isOpen && 'modal-lite--shown')}
            onClick={onClickHandler}
            data-testid="modal"
          >
            <div className={clsx('modal-lite__content', 'modal-lite-' + size)}>
              {showExtraHeader && <Header />}
              {showExtraBody && <Body />}
              {children}
            </div>
          </div>
        </ModalContext.Provider>,
        document.body
      )}
    </>
  )
}

Modal.propTypes = {
  children: PropTypes.node,
  open: PropTypes.bool,
  size: PropTypes.oneOf(['sm', 'md', 'lg', 'xl', 'xxl']),
  title: PropTypes.string,
  content: PropTypes.node,
  iframeSrc: PropTypes.string,
  hasCloseButton: PropTypes.bool,
  onOpen: PropTypes.func,
  onClose: PropTypes.func
}

Modal.defaultProps = {
  children: [],
  open: false,
  size: 'lg'
}

Object.assign(Modal, {
  Header,
  Body,
  Footer
})
